import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidebar } from '@/context/SidebarContext';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { searchService, SearchResult } from '@/services/search.service';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';

type AdminHeaderTitleKey =
  | 'dashboard'
  | 'orders'
  | 'professionalOrders'
  | 'clients'
  | 'transaction'
  | 'report'
  | 'addProducts'
  | 'productsList'
  | 'stock'
  | 'promoCode'
  | 'categories'
  | 'reviews'
  | 'settings'
  | 'authority'
  | 'invoices'
  | 'invoiceDetail'
  | 'users'
  | 'customerBehavior'
  | 'faq'
  | 'testimonials'
  | 'homeBoxes'
  | 'homeComingBoxes';

const ROUTE_TO_TITLE_KEY: Record<string, AdminHeaderTitleKey> = {
  '/admin/dashboard': 'dashboard',
  '/admin/commandes': 'orders',
  '/admin/commandes-professionnelles': 'professionalOrders',
  '/admin/clients': 'clients',
  '/admin/transaction': 'transaction',
  '/admin/rapport': 'report',
  '/admin/ajouter-produit': 'addProducts',
  '/admin/liste-produits': 'productsList',
  '/admin/gestion-stock': 'stock',
  '/admin/code-promo': 'promoCode',
  '/admin/categories': 'categories',
  '/admin/avis-produits': 'reviews',
  '/admin/parametres': 'settings',
  '/admin/autorite-controle': 'authority',
  '/admin/factures': 'invoices',
  '/admin/utilisateurs': 'users',
  '/admin/analytics/customer-behavior': 'customerBehavior',
  '/admin/cms/faq': 'faq',
  '/admin/cms/testimonials': 'testimonials',
  '/admin/cms/home-boxes': 'homeBoxes',
  '/admin/cms/home-coming-boxes': 'homeComingBoxes',
};

/** Détail facture : /admin/factures/:id */
const resolveAdminHeaderTitleKey = (pathname: string): AdminHeaderTitleKey => {
  if (
    pathname.startsWith('/admin/factures/') &&
    pathname.slice('/admin/factures/'.length).length > 0
  ) {
    return 'invoiceDetail';
  }
  return ROUTE_TO_TITLE_KEY[pathname] ?? 'dashboard';
};

const getPageTitleParts = (
  pathname: string,
  t: (key: string) => string
): { light: string; strong: string } => {
  const key = resolveAdminHeaderTitleKey(pathname);
  return {
    light: t(`dashboardHeader.titles.${key}.light`),
    strong: t(`dashboardHeader.titles.${key}.strong`),
  };
};

export const DashboardHeader = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitleParts = getPageTitleParts(location.pathname, t);
  const showToggle = location.pathname === '/admin/ajouter-produit';
  const { isOpen, toggleSidebar } = useSidebar();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Recherche avec debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchService.searchAll(searchQuery);
        setSearchResults(results);
        setShowResults(results.length > 0);
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Fermer les résultats quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    setSearchQuery('');
    setShowResults(false);
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleResultClick(searchResults[0]);
    } else if (e.key === 'Escape') {
      setShowResults(false);
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

      return (
        <header className="dashboard-header">
          <div className="header-left">
            <button className="sidebar-toggle-btn" onClick={toggleSidebar} aria-label={t('dashboardHeader.toggleSidebar')}>
              {isOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
            </button>
            <h1 className="gtitle dashboard-header-gtitle dashboard-header-page-title">
              <span className="gtitle-light">{pageTitleParts.light}</span>
              <span className="gtitle-strong">{pageTitleParts.strong}</span>
            </h1>
          </div>
          <div className="header-right">
        {/* Input de recherche en forme de capsule - Caché sur mobile/tablette */}
        <div className="search-input-wrapper desktop-search" ref={searchWrapperRef}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('dashboardHeader.searchPlaceholder')}
            className="search-input"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowResults(true);
              }
            }}
          />
          <svg 
            className="search-icon" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth="2" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" 
            />
          </svg>
          
          {/* Résultats de recherche */}
          {showResults && searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((result, index) => (
                <div
                  key={`${result.type}-${result.id}-${index}`}
                  className="search-result-item"
                  onClick={() => handleResultClick(result)}
                >
                  <div className="search-result-icon">
                    {result.type === 'order' && '📦'}
                    {result.type === 'product' && '🛍️'}
                    {result.type === 'client' && '👤'}
                    {result.type === 'invoice' && '📄'}
                  </div>
                  <div className="search-result-content">
                    <div className="search-result-title">{result.title}</div>
                    {result.subtitle && (
                      <div className="search-result-subtitle">{result.subtitle}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {showResults && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div className="search-results">
              <div className="search-result-empty">
                {t('dashboardHeader.noResults', { query: searchQuery })}
              </div>
            </div>
          )}
        </div>
        <LanguageSwitcher className="language-switcher-inline" />
        
        <button className="icon-button">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="20" height="20">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
          </svg>
        </button>
        {showToggle && (
          <div className="toggle-switch-wrapper">
            <label className="toggle-switch">
              <input type="checkbox" defaultChecked />
              <span className="toggle-slider"></span>
            </label>
          </div>
        )}

        
      </div>
    </header>
  );
};

